/**
 * SupportForm — public-facing "Email us" form used on Customer Support page.
 * Posts to POST /api/forms/support.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Mail, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SupportForm = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '', booking_id: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required'); return;
    }
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error('Please describe your issue'); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/forms/support`, form);
      setDone(true);
      toast.success("Got it — we'll reply within 24 hours.");
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="rounded-3xl border-2 border-[#FF0059] bg-[#FFE5EE]/40 p-8 text-center" data-testid="support-form-success">
        <CheckCircle2 className="h-12 w-12 text-[#FF0059] mx-auto mb-3" />
        <p className="text-2xl font-black text-black mb-1">Message sent</p>
        <p className="text-gray-600">We'll reply to {form.email} within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl border-2 border-gray-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="support-form">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-full bg-[#FF0059] text-white flex items-center justify-center"><Mail className="h-4 w-4" /></div>
        <h3 className="text-2xl font-black text-black">Email us</h3>
      </div>
      <p className="text-sm text-gray-600 mb-5">Our support team replies within 24 hours, every day of the week.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="support-name" />
        <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="support-email" />
        <Input label="Phone (optional)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="support-phone" />
        <Input label="Booking ID (optional)" value={form.booking_id} onChange={(v) => setForm({ ...form, booking_id: v })} testId="support-booking-id" placeholder="e.g. AB123456" />
      </div>
      <div className="mt-3">
        <Input label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testId="support-subject" />
      </div>
      <div className="mt-3">
        <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Message</label>
        <textarea
          rows={5}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-[#FF0059] outline-none text-sm resize-none"
          placeholder="Tell us what happened…"
          data-testid="support-message"
        />
      </div>
      <Button type="submit" disabled={submitting} className="mt-5 bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold rounded-full" data-testid="support-submit">
        <Send className="h-4 w-4 mr-2" /> {submitting ? 'Sending…' : 'Send message'}
      </Button>
    </form>
  );
};

const Input = ({ label, value, onChange, type = 'text', placeholder, testId }) => (
  <div>
    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1.5">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#FF0059] outline-none text-sm"
      data-testid={testId}
    />
  </div>
);
